/**
 * sessionStorage와 동기화되는 상태 관리 Hook입니다.
 *
 * @remarks
 * 브라우저 세션 동안 상태를 유지해야 할 때 사용합니다.
 * 탭을 닫으면 데이터가 사라집니다.
 *
 * @example
 * ```tsx
 * const [filter, setFilter] = useSessionStorage('searchFilter', '');
 * ```
 */
import { useState } from 'react';

/**
 * sessionStorage와 동기화되는 상태를 관리합니다.
 *
 * @typeParam T - 저장할 값의 타입
 * @param key - sessionStorage 키
 * @param initialValue - 초기값
 * @returns [저장된 값, 값 설정 함수] 튜플
 */
export function useSessionStorage<T>(key: string, initialValue: T) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      // Get from session storage by key
      const item = window.sessionStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch {
      // If error also return initialValue
      // console.log(error);
      return initialValue;
    }
  });
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to sessionStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to session storage
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch {
      // A more advanced implementation would handle the error case
      // console.log(error);
    }
  };
  return [storedValue, setValue] as const;
}
